import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div
      class="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden font-sans bg-cover bg-center"
      [style.backgroundImage]="'url(/img/zanzibar.jpg)'"
    >
      <!-- Backdrop Overlay -->
      <div class="absolute inset-0 bg-slate-950/60 backdrop-blur-[3px] z-0"></div>

      <div class="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <!-- Brand & Seal -->
        <div class="flex flex-col items-center gap-3">
          <div class="flex items-center gap-3 bg-white/95 px-5 py-2.5 rounded-full border border-slate-200 shadow-lg backdrop-blur">
            <img src="/img/smz.png" class="w-10 h-auto" alt="SMZ Government Seal" />
            <div class="h-6 w-px bg-slate-200"></div>
            <div class="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center font-black text-white text-xs">ZX</div>
          </div>
        </div>

        <h2 class="mt-6 text-center text-2xl font-black text-white tracking-tight drop-shadow-md">
          Recover Your Password
        </h2>
        <p class="mt-1 text-center text-xs text-emerald-200 font-extrabold tracking-wider uppercase drop-shadow-sm">
          Zan-X Change &bull; Identity & Access Security
        </p>
      </div>

      <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div class="bg-white/95 p-8 rounded-3xl shadow-2xl border border-slate-200 backdrop-blur-md">
          @if (!submitted()) {
            <form (ngSubmit)="onSubmit()" class="space-y-6">
              <div class="text-center">
                <div class="w-12 h-12 bg-emerald-50 rounded-2xl border border-emerald-200 flex items-center justify-center mx-auto mb-3 text-emerald-600">
                  <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <h3 class="text-base font-bold text-slate-900">Forgot Password?</h3>
                <p class="text-xs text-slate-500 mt-1">
                  Enter your registered institutional email address. We will dispatch a 6-digit One-Time Password (OTP) verification code.
                </p>
              </div>

              @if (errorMessage()) {
                <div class="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
                  <svg class="w-4 h-4 text-rose-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{{ errorMessage() }}</span>
                </div>
              }

              <div>
                <label for="recovery-email" class="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                  Registered Official Email
                </label>
                <div class="relative">
                  <input
                    id="recovery-email"
                    type="email"
                    [(ngModel)]="email"
                    name="email"
                    required
                    placeholder="e.g. officer@institution.go.tz"
                    class="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder-slate-400 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>
              </div>

              <button
                type="submit"
                [disabled]="loading() || !email.trim()"
                class="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm shadow-md shadow-emerald-600/20 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                @if (loading()) {
                  <span class="inline-flex items-center gap-2">
                    <svg class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Dispatching Code...
                  </span>
                } @else {
                  Send Recovery Code (OTP)
                }
              </button>

              <div class="pt-3 border-t border-slate-200 text-center text-xs text-slate-600">
                Remember your password?
                <a routerLink="/login" class="font-bold text-emerald-700 hover:text-emerald-800 hover:underline pl-1">
                  Back to Sign In
                </a>
              </div>
            </form>
          } @else {
            <!-- SUCCESS STATE: OTP Dispatched -->
            <div class="text-center space-y-5">
              <div class="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto text-emerald-700 shadow-inner">
                <svg class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <div>
                <h3 class="text-lg font-bold text-slate-900">Verification Code Sent!</h3>
                <p class="text-xs text-slate-600 mt-2">
                  A 6-digit verification code has been dispatched to:
                </p>
                <div class="mt-2 inline-block px-3.5 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 font-mono font-bold text-xs text-emerald-900">
                  {{ email }}
                </div>
                <p class="text-[11px] text-slate-400 mt-2">
                  This code is valid for 15 minutes. Please check your inbox and spam folder.
                </p>
              </div>

              <div class="pt-2 space-y-3">
                <button
                  type="button"
                  (click)="proceedToReset()"
                  class="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm shadow-md shadow-emerald-600/20 transition-all"
                >
                  Enter Code & Reset Password &rarr;
                </button>

                <button
                  type="button"
                  (click)="submitted.set(false)"
                  class="w-full py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs transition"
                >
                  Use a different email
                </button>
              </div>

              <div class="pt-2 border-t border-slate-200 text-center text-xs text-slate-600">
                <a routerLink="/login" class="font-bold text-emerald-700 hover:text-emerald-800 hover:underline">
                  Return to Sign In
                </a>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  `
})
export class ForgotPasswordComponent {
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private router = inject(Router);

  email = '';
  loading = signal<boolean>(false);
  submitted = signal<boolean>(false);
  errorMessage = signal<string>('');

  onSubmit() {
    if (!this.email || !this.email.trim()) {
      this.errorMessage.set('Please provide a valid official email address.');
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');

    this.authService.forgotPassword({ email: this.email.trim() }).subscribe({
      next: () => {
        this.loading.set(false);
        this.submitted.set(true);
        this.toastService.success('Verification code generated and sent to ' + this.email.trim());
      },
      error: (err) => {
        this.loading.set(false);
        const msg = err.error?.message || 'Could not process password recovery. Please verify the email address or contact support.';
        this.errorMessage.set(msg);
        this.toastService.error(msg);
      }
    });
  }

  proceedToReset() {
    this.router.navigate(['/reset-password'], {
      queryParams: { email: this.email.trim() }
    });
  }
}
